<script setup lang="ts">
  import { toFullUrl, type CompiledFieldNode, type FieldValue } from '@record-fill';

  const props = defineProps<{ node: CompiledFieldNode; disabled: boolean; readOnly?: boolean; inputClass?: string }>();
  const emit = defineEmits<{ (e: 'update', value: FieldValue): void }>();

  const control = props.node.control.kind === 'text' ? props.node.control : null;

  let valueAtFocus: string | null = null;

  function onInput(event: Event): void {
    emit('update', (event.target as HTMLInputElement).value);
  }

  function onFocus(event: Event): void {
    valueAtFocus = (event.target as HTMLInputElement).value;
  }

  // The server stores a URL with its scheme; completing it here keeps a retyped `exemplo.com` over a
  // stored `https://exemplo.com` from counting as a change the server then finds empty. Only a value
  // changed in this focus is completed: crossing a field that holds an `exemplo.com` stored before the
  // scheme was added, or typing and erasing over it, would otherwise write a correction nobody made.
  function onBlur(event: Event): void {
    if (control?.inputType !== 'url' || props.disabled || props.readOnly) return;
    const value = (event.target as HTMLInputElement).value;
    if (value === valueAtFocus) return;
    const fullUrl = toFullUrl(value);
    if (fullUrl !== value) emit('update', fullUrl);
  }
</script>

<template>
  <input
    :type="control?.inputType ?? 'text'"
    :class="[
      inputClass,
      { 'cursor-default': readOnly },
      'w-full min-w-0 text-[11px] leading-4 [font-family:var(--font-base)] placeholder:text-[10px] placeholder:opacity-60 disabled:cursor-not-allowed disabled:bg-[var(--color-gray-09)] disabled:opacity-60',
    ]"
    :value="typeof node.value === 'string' ? node.value : ''"
    :placeholder="control?.placeholder"
    :minlength="control?.minLength"
    :maxlength="control?.maxLength"
    :disabled="disabled"
    :readonly="readOnly"
    :tabindex="readOnly ? -1 : undefined"
    :data-field-id="node.id"
    @input="onInput"
    @focus="onFocus"
    @blur="onBlur"
  >
</template>
